/**
 * UpsertPrescriptionForm - Modal for creating/editing prescriptions
 *
 * UI MIGRATION NOTES (for future reference):
 * ──────────────────────────────────────────
 * This component was migrated from the DraggableModal system to StyledModal.
 *
 * WHAT CHANGED (UI only):
 *  - Replaced: useGlobalModal + openModal() + ModalContentContainer → StyledFormModal (StyledModal)
 *  - Replaced: MUI <Button> → <StyledButtonV2> in footer actions
 *  - Replaced: Nested <Form> component → flattened all hooks/state to parent scope
 *  - Replaced: onClose("prescription-form") → disclosureHost.close()
 *  - Modal sizing moved from openModal({ width, height }) → sx prop on StyledFormModal
 *
 * WHAT DID NOT CHANGE (business logic preserved):
 *  - Formik config, validation schema, initial values
 *  - createPrescription / updatePrescription / archivePrescription API calls
 *  - allergyList and allPrescription data fetching
 *  - onOrder callback, prefillData handling
 *  - All child components: PrescriptionFields, RxCard, AllergyView, DeleteModal
 *  - Props interface (UpsertPrescriptionFormProps) — no consumer changes needed
 *
 * PREVIOUS IMPLEMENTATION (removed):
 *  - import ModalContentContainer from ".../globalModal/ModalContentContainer"
 *  - import { useGlobalModal } from ".../context/GlobalModalProvider"
 *  - import useClose from ".../hooks/global-modal/use-close"
 *  - const { openModal, closeModal } = useGlobalModal()
 *  - const { onClose } = useClose(disclosureHost, closeModal)
 *  - Nested `const Form = ({ params }) => { ... }` component with all hooks inside
 *  - disclosureHost.opened && openModal({ id, title, params, onClose, content, width, height })
 *  - return null
 */

import { CircularProgress, Grid, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import * as Yup from "yup";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import DeleteModal from "../../../../../../../components/app/DeleteModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
// [MIGRATION] Replaced: ModalContentContainer → StyledFormModal
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
// [MIGRATION] Removed: useGlobalModal, useClose (no longer needed with StyledFormModal)
import { useLoadingContext } from "../../../../../../../context/LoadingProvider";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useFormScrollIntoView } from "../../../../../../../hooks/use-form-scrollInto-view";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useCreateOrderPrescription } from "../../../../../../../queries/patient-chart/order/prescription/use-create-order-prescription";
import { useGetPrescriptionFormById } from "../../../../../../../queries/patient-chart/order/prescription/use-get-prescription-form-by-id";
// import { useArchivePrescription } from "../../../../../../../queries/patient-chart/order/prescription/use-prescription-archive";
import { usePrescriptionStatusChange } from "../../../../../../../queries/patient-chart/order/prescription/use-prescription-status-change";
import { useUpdateOrderPrescription } from "../../../../../../../queries/patient-chart/order/prescription/use-update-order-prescription";
import {
  PrescriptionForm,
  UpdatePrescriptionStatusStatusEnum,
} from "../../../../../../../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { PrescriptionFormInitialValues } from "../../../../patient/patient-list/patient-chart-new/order/order-utilsnew";
import { AllergyView } from "./AllergyView";
import { PrescriptionFields } from "./PrescriptionFields";
import { RxCard } from "./RxCard";

interface UpsertPrescriptionFormProps {
  disclosureHost: DisclosureHost;
  readOnly?: boolean;
  prefillData?: any;
  setPrefillData?: any;
  onOrder?: (id: string) => void;
  isVisitNote?: boolean;
  icdCodes?: { value: string; label: string }[];
}

// [MIGRATION] Flattened: Previously had a nested `const Form = ({ params }) => { ... }`
// All hooks and state are now at the parent component level.
// `params.patientId` → `resolvedPatientId`, `params.appointmentId` → `appointmentId`, `params.icdCodes` → `icdCodes` (prop)
const UpsertPrescriptionForm = ({
  readOnly,
  disclosureHost,
  prefillData,
  setPrefillData,
  onOrder: _,
  isVisitNote: _isVisitNote,
  icdCodes,
}: UpsertPrescriptionFormProps) => {
  const appointmentId = useSearchParams()[0].get("appointmentid") as any;

  // TODO: revamp remove this old patientId after new implementation tested.
  const patientIdOld = useSearchParams()[0].get("patientid") as any;
  const { patientId } = useParams();
  const rxID = disclosureHost?.data;

  const [rawPRescribeMeds, setRawPRescribeMds] = useState<any[]>([]);

  // The prescription's own uuid, captured once when the modal opens. Clicking a
  // medication card overwrites `prefillData`/formik values with the medication's
  // uuid; keeping the prescription uuid separately prevents the medication uuid
  // from leaking into the update payload's top-level `uuid` (which would make
  // the PUT target the wrong record and fail).
  const [prescriptionUuid, setPrescriptionUuid] = useState<string | undefined>(
    undefined
  );

  // [MIGRATION] Previously passed as params.patientId in openModal
  // const resolvedPatientId = ;

  const [resolvedPatientId] = useState(patientIdOld ?? patientId);
  const { mutateAsync: createPrescription } = useCreateOrderPrescription();
  // const { mutateAsync: archivePrescription } = useArchivePrescription();
  const { mutateAsync: updatePrescription } = useUpdateOrderPrescription();
  const [hasVisibleClearFormButton, setHasVisibleClearFormButton] =
    useState(false);
  const deleteDisc = useDisclosure();
  const { setIsLoading } = useLoadingContext();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  // const { data: allPrescription } = useGetOrderPrescription({
  //   pagination: DEF_PAGINATION,
  //   patientUuid: isVisitNote ? undefined : resolvedPatientId,
  //   appointmentUuid: isVisitNote ? appointmentId : undefined,
  //   archive: false,
  //   xTENANTID: getXtenantId(),
  //   enabled: disclosureHost.opened,
  // });

  // const { data: allPrescription } = useGetOrderPrescription({

  // Archive API is no longer called from the medication cross icon; the
  // medication is added only in local state before Prescribe, so it must be
  // removed locally instead of hitting the archive endpoint.
  // async function archiveHandler<T>(value: boolean, id: T) {
  //   return archivePrescription({
  //     prescriptionFormId: id?.toString() ?? "",
  //     status: value,
  //   }).then((res) => {
  //     queryClient.invalidateQueries({
  //       queryKey: ["patient-prescription-list"],
  //     });
  //     return res;
  //   });
  // }

  const handleRemoveMedication = async (id?: unknown) => {
    const uuid = id?.toString();
    setRawPRescribeMds((prev) =>
      (prev ?? []).filter((m: any) => m?.uuid !== uuid)
    );
    setSelectedMedIds((prev) => prev.filter((x) => x !== uuid));
    return { message: "Prescription order removed successfully." };
  };

  const savePrescription = (values: PrescriptionForm) => {
    const payload = getUnNecessaryTransformData(
      ["provider", "preferences", "medicalCodes"],
      {
        ...values,
        // Always use the prescription's own uuid, never a medication uuid that
        // may have leaked into formik values via a medication-card edit.
        uuid: prescriptionUuid ?? undefined,
        appointmentId: appointmentId ?? undefined,
        medication: undefined,
        medicationId: values?.medicationId
          ? { [values.medicationId.value]: values.medicationId.label }
          : undefined,
        provider: values?.provider ? [values.provider] : undefined,
        preferences: values?.preferences ? [values.preferences] : undefined,
        // medicalCodes: values?.medicalCodes ? [values.medicalCodes] : undefined,
        patient: resolvedPatientId ? { [resolvedPatientId]: "" } : undefined,
        medicationData: rawPRescribeMeds ?? [],
        // patientMedications:
        //   hasPrescribe && selectedMedIds.length
        //     ? getSelectedPatientMedications()
        //     : undefined,
      }
    );

    const transformedData = {
      prescriptionForm: payload as any,
      xTENANTID: getXtenantId(),
    };

    showToastAndReset(
      prescriptionUuid
        ? updatePrescription(transformedData)
        : createPrescription(transformedData)
    );
  };

  function resetForm() {
    formik.resetForm();
    setPrefillData?.(undefined);
  }

  const { mutateAsync: prescriptionStatusChange } =
    usePrescriptionStatusChange();

  const handleStatusChange = async (
    prescriptionId: string,
    status: UpdatePrescriptionStatusStatusEnum,
    reason?: string
  ) => {
    // showToastOnApiCall({
    //   showSnackbar,
    prescriptionStatusChange({
      prescriptionFormId: prescriptionId,
      status,
      reason,
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: ["patient-prescription-list"],
      });
    });
  };

  const [hasPrescribe, setHasPrescribe] = useState<boolean>(false);

  const [selectedMedIds, setSelectedMedIds] = useState<string[]>([]);

  const toggleMedSelection = (id?: string) => {
    if (!id) return;
    setSelectedMedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // each carrying its uuid + medicationId), sent as patientMedications.
  // const getSelectedPatientMedications = () =>
  //   (allPrescription?.data?.content ?? []).filter((it: any) =>
  //     selectedMedIds.includes(it?.uuid)
  //   );
  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        if (hasPrescribe) {
          const prescriptionId = prescriptionUuid ?? res?.data?.data;
          handleStatusChange(prescriptionId, "ORDERED");
        }

        setIsLoading(true);
        showSnackbar(getSuccessSnackbar(res));
        queryClient
          .invalidateQueries({
            queryKey: ["patient-prescription-list"],
          })

          .finally(() => setIsLoading(false));

        if (hasPrescribe) {
          setHasPrescribe(false);
          handleClose();
        }

        resetForm();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const [me] = useCurrentUser();
  const formik = useFormik<PrescriptionForm>({
    initialValues: prefillData || {
      ...PrescriptionFormInitialValues,
      provider: {
        value: me?.uuid || "",
        label: `${me?.firstName || ""} ${me?.lastName || ""}`,
      },
      medicalCodes: icdCodes ?? [],
    },
    validationSchema: Yup.object({
      medicationId: Yup.object()
        .required("Please select a medication name.")
        .label("Medication Name"),
      provider: Yup.object()
        .required("Please select the provider who will sign the prescription.")
        .label("Provider"),
      prescriptionFormType: Yup.string().required().label("Type"),
      prescriptionDate: Yup.mixed().required().label("Prescription Date"),
      quantity: Yup.number()
        .transform((v, o) => (o === "" || o === null ? undefined : v))
        .typeError("Enter a valid quantity.")
        .positive("Enter a valid quantity.")
        .required("Enter a valid quantity.")
        .label("Quantity"),
      unit: Yup.string()
        .when("quantity", {
          is: (val: unknown) => val !== undefined && val !== null && val !== "",
          then: (s) => s.required(),
          otherwise: (s) => s.notRequired().nullable(),
        })
        .label("Unit"),
      duration: Yup.number()
        .transform((v, o) => (o === "" || o === null ? undefined : v))
        .typeError("Enter a valid duration.")
        .positive("Enter a valid duration.")
        .nullable()
        .label("Duration"),
      refills: Yup.number()
        .transform((v, o) => (o === "" || o === null ? undefined : v))
        .typeError("Enter a valid refill.")
        .min(0, "Enter a valid refill.")
        .nullable()
        .label("Refill"),
      startDate: Yup.mixed()
        .nullable()
        .test(
          "start-today-or-future",
          "Start date must be today or a future date.",
          (value) =>
            !value ||
            !dayjs(value as string)
              .startOf("day")
              .isBefore(dayjs().startOf("day"))
        )
        .label("Start Date"),
      endDate: Yup.mixed()
        .nullable()
        .test(
          "end-after-start",
          "End date must be after start date.",
          function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return dayjs(value as string).isAfter(dayjs(startDate as string));
          }
        )
        .label("End Date"),
      notFillBefore: Yup.mixed()
        .nullable()
        .test(
          "not-fill-before-after-rx-date",
          "Date must be on or after the prescription date.",
          function (value) {
            const { prescriptionDate } = this.parent;
            if (!value || !prescriptionDate) return true;
            return !dayjs(value as string)
              .startOf("day")
              .isBefore(dayjs(prescriptionDate as string).startOf("day"));
          }
        )
        .label("Do not fill before"),
      substitutionsOrDispense: Yup.string()
        .required()
        .label("Substitutions or Dispense"),
      preferences: Yup.object()
        .required("Please select a pharmacy.")
        .label("Pharmacy"),
      medicalCodes: Yup.array()
        .min(1, "Diagnosis (ICD-10) is a required field")
        .required()
        .label("Diagnosis (ICD-10)"),
    }),
    onSubmit: savePrescription,
    enableReinitialize: true,
  });

  useFormScrollIntoView(formik);

  const handleClearForm = () => {
    setHasVisibleClearFormButton(false);
    formik.resetForm();
    setPrefillData?.(undefined);
  };

  // [MIGRATION] Replaced: onClose("prescription-form") which called disclosureHost.close() + closeModal()
  function handleClose() {
    formik.resetForm();
    setSelectedMedIds([]);
    disclosureHost.close();
    setRawPRescribeMds([]);
  }

  const { data: prescriptionIdData, isFetching: isPrescriptionByIdLoading } =
    useGetPrescriptionFormById({
      prescriptionFormId: rxID,
      xTENANTID: getXtenantId(),
      enabled: !!rxID && disclosureHost.opened,
    });

  useEffect(() => {
    setRawPRescribeMds((prescriptionIdData as any)?.data?.medicationData ?? []);
  }, [prescriptionIdData]);

  // Capture the prescription uuid at the moment the modal opens, before a
  // medication-card edit can overwrite `prefillData`. Openers set `prefillData`
  // synchronously with opening the disclosure, so the prescription uuid is
  // available here (rxID is only set by some entry points, prefillData by all).
  useEffect(() => {
    if (disclosureHost.opened) {
      setPrescriptionUuid(rxID ?? prefillData?.uuid);
    } else {
      setPrescriptionUuid(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosureHost.opened, rxID]);

  const prescribeValidationSchema = Yup.object({
    provider: Yup.object()
      .required("Please select the provider who will sign the prescription.")
      .label("Provider"),
    prescriptionFormType: Yup.string().required().label("Type"),
    prescriptionDate: Yup.mixed().required().label("Prescription Date"),
    preferences: Yup.object()
      .required("Please select a pharmacy.")
      .label("Pharmacy"),
    notFillBefore: Yup.mixed()
      .nullable()
      .test(
        "not-fill-before-after-rx-date",
        "Date must be on or after the prescription date.",
        function (value) {
          const { prescriptionDate } = this.parent;
          if (!value || !prescriptionDate) return true;
          return !dayjs(value as string)
            .startOf("day")
            .isBefore(dayjs(prescriptionDate as string).startOf("day"));
        }
      )
      .label("Do not fill before"),
    medicalCodes: Yup.array()
      .min(1, "Diagnosis (ICD-10) is a required field")
      .required()
      .label("Diagnosis (ICD-10)"),
  });

  const handlePrescribe = async () => {
    const schemaToUse =
      selectedMedIds.length === 0
        ? prescribeValidationSchema.concat(addRxValidationSchema)
        : prescribeValidationSchema;
    try {
      await schemaToUse.validate(formik.values, {
        abortEarly: false,
      });
    } catch (err: any) {
      const errors: Record<string, string> = {};
      const touched: Record<string, boolean> = {};
      err?.inner?.forEach((e: any) => {
        if (e.path) {
          errors[e.path] = e.message;
          touched[e.path] = true;
        }
      });
      formik.setErrors(errors as any);
      formik.setTouched(touched as any, false);
      return;
    }

    const selectedMeds = (rawPRescribeMeds ?? [])
      .filter((m: any) => selectedMedIds.includes(m?.uuid))
      .map(({ uuid, ...rest }: any) => rest);

    const v = formik.values as any;
    const payload = getUnNecessaryTransformData(
      ["provider", "preferences", "medicalCodes"],
      {
        ...v,
        // Always use the prescription's own uuid, never a medication uuid that
        // may have leaked into formik values via a medication-card edit.
        uuid: prescriptionUuid ?? undefined,
        appointmentId: appointmentId ?? undefined,
        medication: undefined,
        medicationId: undefined,
        quantity: undefined,
        unit: undefined,
        refills: undefined,
        directionToPatient: undefined,
        startDate: undefined,
        endDate: undefined,
        provider: v?.provider ? [v.provider] : undefined,
        preferences: v?.preferences ? [v.preferences] : undefined,
        patient: resolvedPatientId ? { [resolvedPatientId]: "" } : undefined,
        medicationData: selectedMeds,
      }
    );

    const transformedData = {
      prescriptionForm: payload as any,
      xTENANTID: getXtenantId(),
    };

    setHasPrescribe(true);
    const apiCall = prescriptionUuid
      ? updatePrescription(transformedData)
      : createPrescription(transformedData);

    apiCall
      .then((res: any) => {
        const prescriptionId = prescriptionUuid ?? res?.data?.data;
        if (prescriptionId) {
          handleStatusChange(prescriptionId, "ORDERED");
        }
        setIsLoading(true);
        showSnackbar(getSuccessSnackbar(res));
        queryClient
          .invalidateQueries({ queryKey: ["patient-prescription-list"] })
          .finally(() => setIsLoading(false));
        setHasPrescribe(false);
        handleClose();
      })
      .catch((err: any) => showSnackbar(getErrorSnackbar(err)));
  };

  const addRxValidationSchema = Yup.object({
    medicationId: Yup.object()
      .required("Please select a medication name.")
      .label("Medication Name"),
    prescriptionFormType: Yup.string().required().label("Type"),
    substitutionsOrDispense: Yup.string()
      .required()
      .label("Substitutions or Dispense"),
    quantity: Yup.number()
      .transform((v, o) => (o === "" || o === null ? undefined : v))
      .typeError("Enter a valid quantity.")
      .positive("Enter a valid quantity.")
      .required("Enter a valid quantity.")
      .label("Quantity"),
    unit: Yup.string()
      .when("quantity", {
        is: (val: unknown) => val !== undefined && val !== null && val !== "",
        then: (s) => s.required(),
        otherwise: (s) => s.notRequired().nullable(),
      })
      .label("Unit"),
    duration: Yup.number()
      .transform((v, o) => (o === "" || o === null ? undefined : v))
      .typeError("Enter a valid duration.")
      .positive("Enter a valid duration.")
      .nullable()
      .label("Duration"),
    refills: Yup.number()
      .transform((v, o) => (o === "" || o === null ? undefined : v))
      .typeError("Enter a valid refill.")
      .min(0, "Enter a valid refill.")
      .nullable()
      .label("Refill"),
    startDate: Yup.mixed()
      .nullable()
      .test(
        "start-today-or-future",
        "Start date must be today or a future date.",
        (value) =>
          !value ||
          !dayjs(value as string)
            .startOf("day")
            .isBefore(dayjs().startOf("day"))
      )
      .label("Start Date"),
    endDate: Yup.mixed()
      .nullable()
      .test(
        "end-after-start",
        "End date must be after start date.",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return dayjs(value as string).isAfter(dayjs(startDate as string));
        }
      )
      .label("End Date"),
  });

  const handleSaveAsDraft = async () => {
    const hasSideMeds = (rawPRescribeMeds ?? []).length > 0;
    const schemaToUse = hasSideMeds
      ? prescribeValidationSchema
      : prescribeValidationSchema.concat(addRxValidationSchema);

    try {
      await schemaToUse.validate(formik.values, { abortEarly: false });
    } catch (err: any) {
      const errors: Record<string, string> = {};
      const touched: Record<string, boolean> = {};
      err?.inner?.forEach((e: any) => {
        if (e.path) {
          errors[e.path] = e.message;
          touched[e.path] = true;
        }
      });
      formik.setErrors(errors as any);
      formik.setTouched(touched as any, false);
      return;
    }

    const v = formik.values as any;
    const payload = getUnNecessaryTransformData(
      ["provider", "preferences", "medicalCodes"],
      {
        ...v,
        // Always use the prescription's own uuid, never a medication uuid that
        // may have leaked into formik values via a medication-card edit.
        uuid: prescriptionUuid ?? undefined,
        appointmentId: appointmentId ?? undefined,
        medication: undefined,
        medicationId: hasSideMeds
          ? undefined
          : v?.medicationId
            ? { [v.medicationId.value]: v.medicationId.label }
            : undefined,
        quantity: hasSideMeds ? undefined : v.quantity,
        unit: hasSideMeds ? undefined : v.unit,
        refills: hasSideMeds ? undefined : v.refills,
        directionToPatient: hasSideMeds ? undefined : v.directionToPatient,
        startDate: hasSideMeds ? undefined : v.startDate,
        endDate: hasSideMeds ? undefined : v.endDate,
        provider: v?.provider ? [v.provider] : undefined,
        preferences: v?.preferences ? [v.preferences] : undefined,
        patient: resolvedPatientId ? { [resolvedPatientId]: "" } : undefined,
        medicationData: hasSideMeds
          ? (rawPRescribeMeds ?? []).map(({ uuid, ...rest }: any) => rest)
          : [],
      }
    );

    const transformedData = {
      prescriptionForm: payload as any,
      xTENANTID: getXtenantId(),
    };

    const apiCall = prescriptionUuid
      ? updatePrescription(transformedData)
      : createPrescription(transformedData);

    apiCall
      .then((res: any) => {
        setIsLoading(true);
        showSnackbar(getSuccessSnackbar(res));
        queryClient
          .invalidateQueries({ queryKey: ["patient-prescription-list"] })
          .finally(() => setIsLoading(false));
        handleClose();
      })
      .catch((err: any) => showSnackbar(getErrorSnackbar(err)));
  };

  const handleAddRx = async () => {
    try {
      await addRxValidationSchema.validate(formik.values, {
        abortEarly: false,
      });
    } catch (err: any) {
      const errors: Record<string, string> = {};
      const touched: Record<string, boolean> = {};
      err?.inner?.forEach((e: any) => {
        if (e.path) {
          errors[e.path] = e.message;
          touched[e.path] = true;
        }
      });
      formik.setErrors({ ...formik.errors, ...errors } as any);
      formik.setTouched({ ...formik.touched, ...touched } as any, false);
      return;
    }

    const v = formik.values as any;
    const med = {
      uuid:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      medicationId: {
        [v.medicationId.value]: v.medicationId.label,
      },
      prescriptionFormType: v.prescriptionFormType,
      quantity: v.quantity,
      directionToPatient: v.directionToPatient,
      unit: v.unit,
      refills: v.refills,
      duration: v.duration,
      substitutionsOrDispense: v.substitutionsOrDispense,
      startDate: v.startDate,
      endDate: v.endDate,
    };
    setRawPRescribeMds((prev) => [...(prev ?? []), med]);

    formik.setFieldValue("medicationId", undefined);
    formik.setFieldValue("quantity", "");
    formik.setFieldValue("unit", "");
    formik.setFieldValue("refills", "");
    formik.setFieldValue("directionToPatient", "");
    formik.setFieldValue("startDate", undefined);
    formik.setFieldValue("endDate", undefined);
    formik.setFieldValue("duration", undefined);
    formik.setFieldValue("substitutionsOrDispense", undefined);

    // The medication fields are cleared, so they must go back to untouched too,
    // otherwise a field the user already blurred (Substitutions or Dispense)
    // instantly renders its "required" error on the freshly emptied form.
    formik.setTouched({}, false);
  };

  /**
   * [MIGRATION] Previously rendered via:
   *   disclosureHost.opened && openModal({
   *     id: "prescription-form", title: "Prescription Form",
   *     params: { patientId: patientIdOld ?? patientId, appointmentId, icdCodes },
   *     onClose: () => onClose("prescription-form"),
   *     content: (params) => <Form params={params} />,
   *     height: "80vh", width: "1200px",
   *   });
   *   return null;
   *
   * Now renders directly as <StyledFormModal> with same dimensions and content.
   */
  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Prescription Form"
      dialogAction={
        !readOnly ? (
          <Flex gap={2} height={"34px"} justifyContent={"end"}>
            {hasVisibleClearFormButton && (
              <StyledButtonV2
                label="Clear"
                onClick={handleClearForm}
                variant="outlined"
              />
            )}
            {/* [MIGRATION] Replaced: MUI <Button variant="outlined"> → <StyledButtonV2> */}
            <Tooltip
              title={
                selectedMedIds.length > 0
                  ? "Uncheck medications to save as draft"
                  : ""
              }
            >
              <span>
                <StyledButtonV2
                  label="Save As Draft"
                  variant="outlined"
                  onClick={handleSaveAsDraft}
                  disabled={selectedMedIds.length > 0}
                />
              </span>
            </Tooltip>
            <StyledButtonV2
              label={prefillData ? "Update Rx" : "Add Rx"}
              variant="outlined"
              onClick={handleAddRx}
              sx={{
                width: "110px",
                backgroundColor: "#CCECFF80",
                color: "#004186",
              }}
            />

            <StyledButtonV2
              label="Prescribe"
              variant="primary"
              onClick={handlePrescribe}
            />

            {/* {prefillData && (
              <StyledButtonV2
                label="Order"
                variant="contained"
                onClick={() => {
                  onOrder?.(prefillData?.uuid);
                  // [MIGRATION] Previously: onClose("prescription-form")
                  disclosureHost.close();
                }}
                sx={{ width: "90px" }}
              />
            )} */}
          </Flex>
        ) : undefined
      }
      sx={{
        // [MIGRATION] Previously: openModal({ width: "1200px", height: "80vh" })
        "& .MuiDialog-paper": {
          width: "1200px",
          height: "80vh",
        },
      }}
    >
      <Grid container spacing={2} pt={2}>
        <Grid item xs={4}>
          <Flex
            gap={2}
            height={"calc(100vh - 138px)"}
            borderRadius={1}
            width={"380px"}
            p={1}
            flexDirection={"column"}
            justifyContent={"space-between"}
            bgcolor={"#1A1A1A05"}
          >
            <Flex
              gap={2}
              height={"calc(100vh - 138px)"}
              borderRadius={1}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Flex
                flexDirection={"column"}
                gap={2}
                border={"solid 1px #1A1A1A26"}
                height={"50%"}
                bgcolor={"#1A1A1A05"}
                p={1}
              >
                <DeleteModal
                  w="380px"
                  open={!!deleteDisc?.opened}
                  onClose={deleteDisc?.close!}
                  id={deleteDisc?.data}
                  onConfirmation={(id) => handleRemoveMedication(id)}
                  content={"You want to delete this Prescription Order"}
                />

                <Text fontWeight={"medium"}>MEDICATIONS</Text>
                <Flex
                  flexDirection={"column"}
                  gap={1}
                  sx={{ overflowY: "auto", overflowX: "unset" }}
                >
                  {isPrescriptionByIdLoading ? (
                    <Flex justifyContent="center" alignItems="center" p={2}>
                      <CircularProgress size={24} />
                    </Flex>
                  ) : (
                    rawPRescribeMeds?.map((it) => (
                      <RxCard
                        key={it?.uuid}
                        med={it}
                        selectable
                        selected={selectedMedIds.includes(it?.uuid ?? "")}
                        onToggleSelect={toggleMedSelection}
                        onDelete={(id) => deleteDisc.open(id)}
                        onEdit={(med) => {
                          // Prefill only the Type/Medication Name + Instructions
                          // fields from the selected medication. Merge into the
                          // current values so prescription-level fields (provider,
                          // pharmacy, diagnoses, prescription date, etc.) and the
                          // prescription uuid are preserved.
                          formik.setValues((prev: any) => ({
                            ...prev,
                            prescriptionFormType:
                              med?.prescriptionFormType ??
                              prev?.prescriptionFormType,
                            medicationId: med?.medicationId,
                            directionToPatient: med?.directionToPatient ?? "",
                            duration: med?.duration ?? undefined,
                            quantity: med?.quantity ?? "",
                            unit: med?.unit ?? "",
                            refills: med?.refills ?? "",
                            startDate: med?.startDate ?? undefined,
                            endDate: med?.endDate ?? undefined,
                            substitutionsOrDispense:
                              med?.substitutionsOrDispense ?? undefined,
                          }));
                          setHasVisibleClearFormButton(true);
                        }}
                      />
                    ))
                  )}
                </Flex>
              </Flex>

              {disclosureHost?.opened && <AllergyView />}
            </Flex>
          </Flex>
        </Grid>
        <Grid item xs={8}>
          <PrescriptionFields
            readOnly={readOnly}
            handleBlur={formik.handleBlur}
            values={formik.values as any}
            touched={formik.touched}
            errors={formik.errors}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            icdCodes={icdCodes}
          />
        </Grid>
        {/* <Grid item xs={3} height={"50%"}>
          <Box width={"302px"}>
          </Box>
        </Grid> */}
      </Grid>
    </StyledFormModal>
  );
};

export default UpsertPrescriptionForm;
